// client/src/utils/componentMatcher.js - Similarity scoring for catalog component recommendations

// Tolerance ranges for parameter normalization (what we consider "acceptable" deviation)
const TOLERANCE_RANGES = {
  gain: 3.0,        // ±3 dB
  noiseFigure: 0.5, // ±0.5 dB
  p1db: 5.0,        // ±5 dB
  oip3: 5.0         // ±5 dB
};

// Component-type-specific parameter weights
// Higher weight = more important for that component type
const TYPE_WEIGHTS = {
  lna: { noiseFigure: 0.40, gain: 0.25, p1db: 0.20, oip3: 0.15 },
  gainBlock: { gain: 0.30, oip3: 0.30, p1db: 0.25, noiseFigure: 0.15 },
  driver: { oip3: 0.35, p1db: 0.30, gain: 0.20, noiseFigure: 0.15 },
  powerAmplifier: { p1db: 0.35, oip3: 0.30, gain: 0.25, noiseFigure: 0.10 },
  vga: { gain: 0.35, oip3: 0.25, p1db: 0.25, noiseFigure: 0.15 }
};

// Direction preferences: for some params, higher is better; for others, lower is better
const PARAM_DIRECTION = {
  gain: 'higher',       // Higher is better (more gain is desirable)
  noiseFigure: 'lower', // Lower is better
  p1db: 'higher',       // Higher is better
  oip3: 'higher'        // Higher is better
};

/**
 * Calculate similarity score between generic component params and catalog component params
 * Only penalizes when catalog is WORSE than generic (per PARAM_DIRECTION)
 *
 * @param {Object} genericParams - { gain, noiseFigure, p1db, oip3 } from generic component
 * @param {Object} catalogParams - { gain, noiseFigure, p1db, oip3 } interpolated from catalog
 * @param {string} baseType - Component type (lna, gainBlock, driver, powerAmplifier, vga)
 * @returns {Object} { score, confidence, paramDetails }
 */
export function calculateSimilarity(genericParams, catalogParams, baseType) {
  const weights = TYPE_WEIGHTS[baseType] || TYPE_WEIGHTS.gainBlock;
  const params = ['gain', 'noiseFigure', 'p1db', 'oip3'];

  let weightedSumSquares = 0;
  let totalWeight = 0;
  let availableParams = 0;
  const paramDetails = {};

  for (const param of params) {
    const generic = genericParams[param];
    const catalog = catalogParams[param];
    const weight = weights[param] || 0.25;

    // Skip if either value is null/undefined
    if (generic == null || catalog == null) {
      paramDetails[param] = { generic, catalog, diff: null, penalty: 0, quality: 'missing' };
      continue;
    }

    const diff = catalog - generic;
    const tolerance = TOLERANCE_RANGES[param];
    const direction = PARAM_DIRECTION[param];

    // Determine penalty based on direction preference
    let penalty = 0;

    if (direction === 'lower') {
      // Lower is better (e.g., NF) - penalize if catalog is higher than generic
      if (diff > 0) {
        penalty = diff / tolerance;
      }
    } else if (direction === 'higher') {
      // Higher is better (e.g., P1dB, OIP3) - penalize if catalog is lower than generic
      if (diff < 0) {
        penalty = Math.abs(diff) / tolerance;
      }
    } else {
      // Match closely (e.g., gain) - penalize any deviation
      penalty = Math.abs(diff) / tolerance;
    }

    weightedSumSquares += weight * (penalty * penalty);
    totalWeight += weight;
    availableParams++;

    // Determine quality label
    let quality = 'poor';
    if (penalty === 0) {
      quality = 'excellent'; // Meets or exceeds
    } else if (penalty <= 0.3) {
      quality = 'good';
    } else if (penalty <= 0.7) {
      quality = 'fair';
    }

    paramDetails[param] = {
      generic,
      catalog,
      diff,
      penalty,
      quality,
      meetsOrExceeds: penalty === 0
    };
  }

  // No valid comparisons possible
  if (totalWeight === 0 || availableParams === 0) {
    return { score: 0, confidence: 0, matchedParams: 0, paramDetails };
  }

  // Calculate weighted distance
  const weightedDistance = Math.sqrt(weightedSumSquares / totalWeight);

  // Convert distance to score (0-100 scale)
  // score = 100 / (1 + distance) gives us a 0-100 range
  const rawScore = 100 / (1 + weightedDistance);

  // Apply confidence penalty for missing data
  const completeness = availableParams / params.length;
  const confidencePenalty = Math.sqrt(completeness); // Gentler penalty

  const finalScore = Math.round(rawScore * confidencePenalty);

  return {
    score: finalScore,
    rawScore: Math.round(rawScore),
    confidence: Math.round(completeness * 100),
    matchedParams: availableParams,
    paramDetails
  };
}

/**
 * Extract target parameters from a generic component node
 * @param {Object} node - React Flow node with data.parameters
 * @returns {Object} { gain, noiseFigure, p1db, oip3 }
 */
export function extractGenericParams(node) {
  const params = node.data?.parameters || {};
  return {
    gain: params.gain?.value ?? null,
    noiseFigure: params.noiseFigure?.value ?? null,
    p1db: params.p1db?.value ?? null,
    oip3: params.oip3?.value ?? null
  };
}

/**
 * Get score quality label and color
 * @param {number} score - Score 0-100
 * @returns {Object} { label, color }
 */
export function getScoreQuality(score) {
  if (score >= 85) return { label: 'Excellent', color: '#22c55e' }; // Green
  if (score >= 70) return { label: 'Good', color: '#3b82f6' };      // Blue
  if (score >= 50) return { label: 'Fair', color: '#eab308' };      // Yellow
  return { label: 'Poor', color: '#f97316' };                        // Orange
}

/**
 * Score and rank recommendations
 * @param {Array} catalogComponents - Array from API with interpolatedParams
 * @param {Object} genericParams - { gain, noiseFigure, p1db, oip3 }
 * @param {string} baseType - Component type
 * @param {number} maxResults - Maximum number of results to return (default 10)
 * @returns {Array} Sorted array with scores added, limited to top maxResults
 */
export function scoreAndRankRecommendations(catalogComponents, genericParams, baseType, maxResults = 10) {
  const scored = catalogComponents
    .map(comp => {
      const result = calculateSimilarity(genericParams, comp.interpolatedParams, baseType);
      return {
        ...comp,
        matchScore: result.score,
        confidence: result.confidence,
        paramDetails: result.paramDetails,
        scoreQuality: getScoreQuality(result.score)
      };
    })
    .sort((a, b) => b.matchScore - a.matchScore)
    .slice(0, maxResults);

  return scored;
}
